import React, { useState } from 'react';
import { submitRecruitmentForm } from '../utils/api';
import CompanyLogo from '../assets/logo/codecube_tech_largeLogo.png';
import { toast } from 'react-toastify';

const Recruitment = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    gender: '',
    phone: '',
    email: '',
    collegeName: '',
    enrollmentNo: '',
    qualification: '',
    currentSemester: '',
    department: '',
    resume: null
  });

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [submissionSuccess, setSubmissionSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'resume' ? (files && files[0] ? files[0] : null) : value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    if (apiError) setApiError('');
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['firstName', 'lastName', 'gender', 'phone', 'email', 
                           'collegeName', 'enrollmentNo', 'qualification', 'currentSemester', 'department'];
    
    requiredFields.forEach(field => {
      if (!formData[field]?.trim()) {
        newErrors[field] = `${field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} is required`;
      }
    });

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.phone && !/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    setSubmissionSuccess('');

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await submitRecruitmentForm(formData);
      
      setSubmissionSuccess(response.message || 'Application submitted successfully!');
      toast.success('Application submitted successfully!');
      setFormData({
        firstName: '',
        middleName: '',
        lastName: '',
        gender: '',
        phone: '',
        email: '',
        collegeName: '',
        enrollmentNo: '',
        qualification: '',
        currentSemester: '',
        department: '',
        resume: null
      });
      setErrors({});
    } catch (error) {
      if (error instanceof Error) {
        setApiError(error.message);
        toast.error(error.message);
      } else {
        setApiError('An unexpected error occurred. Please try again.');
        toast.error('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500";
  const errorInputClass = "mt-1 block w-full px-3 py-2 border border-red-500 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500";

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg p-6 md:p-8">
          <div className="text-center mb-8 ">
            <div className='border-b-2 border-gray-300 mb-4'>
            <img src={CompanyLogo} alt="Codecube Technology Logo" className="mx-auto mb-4 w-1/2" />
            </div>
            <h1 className="text-xl uppercase font-bold text-indigo-600 mb-2">
              Recruitment Form
            </h1>
            <p className="text-gray-600">
              Thank you for your interest in joining Codecube Technology! Please complete this application form with correct information. 
              We will get in touch with shortlisted candidates for the next steps.
            </p>
          </div>

          {apiError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600 font-medium">{apiError}</p>
            </div>
          )}

          {submissionSuccess && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
              <p className="text-green-600 font-medium">{submissionSuccess}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6 border-t-2 pt-4 border-gray-300 p-3">
            {/* Name Section */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Name <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="First Name"
                    className={errors.firstName ? errorInputClass : inputClass}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <input
                    type="text"
                    name="middleName"
                    value={formData.middleName}
                    onChange={handleChange}
                    placeholder="Middle Name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Last Name"
                    className={errors.lastName ? errorInputClass : inputClass}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Gender <span className="text-red-500">*</span>
              </label>
              <div className="mt-2 space-x-4">
                {['Female', 'Male', 'Other'].map((option) => (
                  <label key={option} className="inline-flex items-center">
                    <input
                      type="radio"
                      name="gender"
                      value={option}
                      checked={formData.gender === option}
                      onChange={handleChange}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
              {errors.gender && (
                <p className="mt-1 text-sm text-red-600">{errors.gender}</p>
              )}
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="10-digit phone number"
                  className={errors.phone ? errorInputClass : inputClass}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  className={errors.email ? errorInputClass : inputClass}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
            </div>

            {/* College Information */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                College/University Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="collegeName"
                value={formData.collegeName}
                onChange={handleChange}
                placeholder="Enter college/university name"
                className={errors.collegeName ? errorInputClass : inputClass}
              />
              {errors.collegeName && (
                <p className="mt-1 text-sm text-red-600">{errors.collegeName}</p>
              )}
            </div>

            {/* Enrollment and Qualification */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Student Enrollment / Roll No <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="enrollmentNo"
                  value={formData.enrollmentNo}
                  onChange={handleChange}
                  placeholder="Enter enrollment/roll number"
                  className={errors.enrollmentNo ? errorInputClass : inputClass}
                />
                {errors.enrollmentNo && (
                  <p className="mt-1 text-sm text-red-600">{errors.enrollmentNo}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Qualification <span className="text-red-500">*</span>
                </label>
                <select
                  name="qualification"
                  value={formData.qualification}
                  onChange={handleChange}
                  className={errors.qualification ? errorInputClass : inputClass}
                >
                  <option value="">Select Qualification</option>
                  <option value="High School">High School</option>
                  <option value="Diploma">Diploma</option>
                  <option value="Bachelor's Degree">Bachelor's Degree</option>
                  <option value="Master's Degree">Master's Degree</option>
                  <option value="PhD">PhD</option>
                  <option value="Other">Other</option>
                </select>
                {errors.qualification && (
                  <p className="mt-1 text-sm text-red-600">{errors.qualification}</p>
                )}
              </div>
            </div>

            {/* Semester and Department */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Current Semester <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="currentSemester"
                  value={formData.currentSemester}
                  onChange={handleChange}
                  placeholder="e.g., 5th Semester"
                  className={errors.currentSemester ? errorInputClass : inputClass}
                />
                {errors.currentSemester && (
                  <p className="mt-1 text-sm text-red-600">{errors.currentSemester}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Faculty/Department Of Study <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  placeholder="Enter your department"
                  className={errors.department ? errorInputClass : inputClass}
                />
                {errors.department && (
                  <p className="mt-1 text-sm text-red-600">{errors.department}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Upload CV / Resume
              </label>
              <input
                type="file"
                name="resume"
                accept=".pdf,.doc,.docx"
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit Application'
                )}
              </button>
            </div>
          </form>

          {/* Add this right after the closing </form> tag in your Recruitment.jsx */}

<div className="mt-12 pt-8 border-t border-gray-200">
  <div className="text-center">
    <h3 className="text-lg font-semibold text-gray-800">
      CC &nbsp;<span className="text-indigo-600">Technology</span>
    </h3>
    <div className="mt-4 space-y-2 text-gray-600">
      <p className="text-sm">
        Laxminiya Rural Municipality-7, Janakpur, Dhanusha, Nepal
      </p>
      <p className="text-sm">
        📞 +977 9819931223 | ✉️ contact@cctechnology.com.np
      </p>
      <p className="text-xs text-gray-500 mt-4">
        © {new Date().getFullYear()} Codecube Technology. All rights reserved.
      </p>
    </div>
  </div>
</div>
        </div>
      </div>
    </div>
  );
};

export default Recruitment;